import {
  LIST_ASSETS,
  RoutineType,
  SortDirectionEnum,
  useCustomQuery,
  useStatefulWorkspaceRoutines,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useExportHook, useExportProps, useExportResult } from './types';
import { getDateFilter } from './dateFilterUtils';

export const useAssetExport: useExportHook = (
  props: useExportProps
): useExportResult => {
  const [queryEnabled, setQueryEnabled] = useState(false);
  const [rows, setRows] = useState([]);
  const routines = useStatefulWorkspaceRoutines(
    props.workspace.id,
    !props.download
  );
  const [headers, setHeaders] = useState<any[]>([
    { label: 'ID', key: 'id' },
    { label: 'Name', key: 'name' },
    { label: 'Description', key: 'description' },
    { label: 'Last Address', key: 'lastAddress' },
    { label: 'Last Scanned Date', key: 'lastScannedDate' },
    { label: 'Last Scanned Time', key: 'lastScannedTime' },
    { label: 'Last Updated Date', key: 'lastUpdatedDate' },
    { label: 'Last Updated Time', key: 'lastUpdatedTime' },
    { label: '# Notes', key: 'numNotes' },
    { label: '# Alerts', key: 'numAlerts' },
    { label: '# Reports', key: 'numReportsRed' },
    { label: '# Alerts', key: 'numReportsYellow' },
    { label: '# Routines', key: 'numRoutines' },
    { label: 'Groups', key: 'groups' },
    { label: 'Tag Code', key: 'tagCode' },
  ]);
  const [columns, setColumns] = useState<string[]>([
    'description',
    'lastAddress',
    'lastScannedAt',
    'lastUpdated',
    'reports',
    'notes',
    'alerts',
    'routines',
    'groups',
    'tagCode',
  ]);

  useEffect(() => {
    if (!routines || routines.length === 0) {
      setQueryEnabled(true);
      return;
    }

    const routineHeaders: any[] = [];
    const routineColumns: any[] = [];
    routines.forEach((routine) => {
      routineHeaders.push({
        label: routine.config.title ?? routine.name,
        key: `routine:${routine.id}`,
      });
      routineColumns.push(`routine:${routine.id}`);
    });

    if (routineHeaders.length > 0) {
      setHeaders([...headers, ...routineHeaders]);
      setColumns([...columns, ...routineColumns]);
    }

    setQueryEnabled(true);
  }, [routines]);

  const { data: assetsToExport, isLoading } = useCustomQuery({
    query: LIST_ASSETS,
    cacheKey: ['exportAssets', props.workspace.id, props.dateFilter],
    variables: {
      filter: {
        workspaceId: props.workspace.id,
        columns: columns,
        ...getDateFilter(props.dateFilter),
      },
      orderBy: { field: 'name', order: SortDirectionEnum.Asc },
      page: { limit: 10000, offset: 0 },
    },
    options: {
      enabled: queryEnabled && props.download,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  useEffect(() => {
    if (!assetsToExport) return;

    const exportRows = assetsToExport.asset.listAssets.items.map(
      (item: any) => {
        const row: any = {};
        Object.keys(item).forEach((key) => {
          if (key === 'groups') {
            const groups = item[key];
            row[key] = groups
              ? groups.map((group: any) => group.name).join(', ')
              : '';
          } else if (key === 'lastScannedAt' && item[key]) {
            const date = new Date(item[key]);
            row['lastScannedDate'] = date.toISOString().split('T')[0]; // YYYY-MM-DD
            row['lastScannedTime'] = date.toISOString().split('T')[1].substring(0, 8); // HH:MM:SS
          } else if (key === 'lastUpdated' && item[key]) {
            const date = new Date(item[key]);
            row['lastUpdatedDate'] = date.toISOString().split('T')[0]; // YYYY-MM-DD
            row['lastUpdatedTime'] = date.toISOString().split('T')[1].substring(0, 8); // HH:MM:SS
          } else if (key !== 'routineState') {
            row[key] = item[key];
          }
        });

        if (item.routineState) {
          item.routineState.forEach((routineState: any) => {
            const routine = routines.find(
              (r) => r.id === routineState.workspaceRoutineId
            );
            const state = routineState.state;
            let value: string | undefined = undefined;

            if (routine?.type === RoutineType.Counter) {
              value = state.count;
            } else if (routine?.type === RoutineType.Status) {
              value = state.status;
            }

            if (value !== undefined) {
              row[`routine:${routine?.id}`] = value;
            }
          });
        }

        return row;
      }
    );

    setRows(exportRows);
    props.onDataReady();
  }, [assetsToExport, routines]);

  return {
    headers,
    rows,
    isLoading,
  };
};
